import React, { useEffect, useState, useCallback } from 'react';
import { Download, CheckCircle2 } from 'lucide-react';

/**
 * InstallAppButton — surfaces the browser's "install PWA" prompt.
 *
 * Behaviour:
 *  - Listens for the `beforeinstallprompt` event (Chrome/Edge/Android).
 *  - When the prompt is available, renders a discreet "Installa app" button.
 *  - When the user accepts, the prompt is consumed; if they dismiss, the
 *    button stays available for the rest of the session.
 *  - When the app is already installed (display-mode: standalone) the
 *    component renders nothing — no need to bug the user.
 *
 * For Safari/iOS, where `beforeinstallprompt` is not supported, the button
 * stays hidden (Apple uses the native Share → "Add to Home Screen" flow).
 */
export default function InstallAppButton({ className = '' }) {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    // Detect already-installed mode
    const standalone =
      window.matchMedia?.('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    if (standalone) setInstalled(true);

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onAppInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onAppInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onAppInstalled);
    };
  }, []);

  const onClick = useCallback(async () => {
    if (!deferred) return;
    try {
      deferred.prompt();
      await deferred.userChoice;
    } catch (e) { /* ignore */ }
    setDeferred(null);
  }, [deferred]);

  if (installed) {
    return null;
  }
  if (!deferred) {
    return null;
  }
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="install-app-button"
      className={
        'inline-flex items-center gap-1.5 rounded-full border border-orange-200 bg-orange-50 text-orange-700 hover:bg-orange-100 px-3 py-1.5 text-sm font-medium transition-colors ' +
        className
      }
      title="Installa l'app sul tuo dispositivo"
    >
      <Download className="h-4 w-4" /> Installa app
    </button>
  );
}

/**
 * InstalledBadge — when the app IS installed in standalone, this renders a
 * small green pill confirming the state. Optional, used in settings page.
 */
export function InstalledBadge() {
  const [installed, setInstalled] = useState(false);
  useEffect(() => {
    const check = () =>
      setInstalled(
        window.matchMedia?.('(display-mode: standalone)').matches ||
          window.navigator.standalone === true,
      );
    check();
    const mq = window.matchMedia?.('(display-mode: standalone)');
    mq?.addEventListener?.('change', check);
    return () => mq?.removeEventListener?.('change', check);
  }, []);
  if (!installed) return null;
  return (
    <span className="inline-flex items-center gap-1 text-xs rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5">
      <CheckCircle2 className="h-3 w-3" /> App installata
    </span>
  );
}
